import { useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { resendESignature } from "@/features/esignatures/api";
import type { ESignatureRequest } from "@/features/esignatures/types";
import { ApiError } from "@/lib/api";

interface Props {
  request: ESignatureRequest | null;
  onOpenChange: (open: boolean) => void;
  onResent?: () => void;
}

/** Confirm-and-resend the invitation (legacy sign_resend). */
export function ResendDialog({ request, onOpenChange, onResent }: Props) {
  const [busy, setBusy] = useState(false);
  const recipientLine =
    request && request.recipients.length === 1 && request.recipients[0]
      ? request.recipients[0].name
      : `${request?.recipients.length ?? 0} recipients`;

  const handleResend = async () => {
    if (!request) return;
    setBusy(true);
    try {
      await resendESignature(request.id);
      toast.success("Invitation resent.");
      onResent?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't resend.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={!!request} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Resend invitation?</AlertDialogTitle>
          <AlertDialogDescription>
            A fresh signing invitation will be emailed to {recipientLine}, and the
            request status resets to Sent.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <Button onClick={handleResend} disabled={busy}>
            {busy ? "Resending…" : "Resend"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
